import React, { ReactNode } from 'react';
import { mergeClasses, tokens } from '@fluentui/react-components';
import { STROKE_WIDTH_RATIO } from 'src/utils/constants';
import { useCircularProgressStyles } from './CircularProgress.styles';

export interface CircularProgressProps {
  progress: number
  strokeWidthRatio?: number
  progressColor?: string
  trackColor?: string
  children?: ReactNode
  className?: string
  showProgressText?: boolean
}

export const CircularProgress: React.FC<CircularProgressProps> = ({
  progress,
  strokeWidthRatio = STROKE_WIDTH_RATIO,
  progressColor = tokens.colorCompoundBrandForeground1,
  trackColor = tokens.colorNeutralStroke2,
  children,
  className,
  showProgressText = true,
}) => {
  const styles = useCircularProgressStyles();

  // Validate and normalize progress value
  const validProgress = Math.min(Math.max(progress ?? 0, 0), 100);
  const normalizedProgress = validProgress / 100;

  // Validate and normalize stroke width ratio (0-1)
  const validStrokeWidthRatio = Math.min(Math.max(strokeWidthRatio, 0), 1);

  // Calculate circle dimensions
  const viewBoxSize = 100;
  const center = viewBoxSize / 2;
  const radius = 45; // Leave 5% margin from edges
  const circumference = 2 * Math.PI * radius; // ~283

  // Calculate stroke width as percentage of viewBox
  const finalStrokeWidth = validStrokeWidthRatio * viewBoxSize;

  // Calculate progress offset
  const strokeDashoffset = circumference * (1 - normalizedProgress);

  return (
    <div
      className={mergeClasses('circular-progress', styles.base, className)}
      role="progressbar"
      aria-valuenow={validProgress}
      aria-valuemin={0}
      aria-valuemax={100}
      data-testid="circular-progress"
    >
      <svg
        className={styles.svg}
        viewBox={`0 0 ${viewBoxSize} ${viewBoxSize}`}
        preserveAspectRatio="xMidYMid meet"
      >
        {/* Background circle */}
        <circle
          className={styles.backgroundCircle}
          cx={center}
          cy={center}
          r={radius}
          strokeWidth={finalStrokeWidth}
          fill="none"
          style={{ stroke: trackColor }}
          data-testid="circle"
        />
        {/* Progress circle */}
        <circle
          className={styles.progressCircle}
          cx={center}
          cy={center}
          r={radius}
          strokeWidth={finalStrokeWidth}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          style={{ stroke: progressColor }}
          strokeLinecap="round"
          data-testid="circle"
        />
      </svg>
      <div className={styles.content} role="text">
        {children
          || (showProgressText && (
            <span className={styles.contentText}>
              {validProgress}
              %
            </span>
          ))}
      </div>
    </div>
  );
};

export default CircularProgress;
